import React, { useState } from 'react';
import { ArrowRight, Shield, Building, Users, Server, CheckCircle, Lock, Globe } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const EnterpriseSecurityPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const enterpriseSolutions = [
    {
      icon: Building,
      title: "Enterprise Security Architecture",
      description: "Comprehensive security architecture design and implementation for large-scale enterprise environments.",
      features: ["Zero-trust architecture", "Multi-layered defense", "Scalable security controls", "Integration planning"]
    },
    {
      icon: Users,
      title: "Identity & Access Management",
      description: "Enterprise-grade identity management solutions with single sign-on and privileged access controls.",
      features: ["SSO implementation", "Multi-factor authentication", "Privileged access management", "Identity governance"]
    },
    {
      icon: Server,
      title: "Security Operations Center",
      description: "24/7 security monitoring and incident response capabilities with dedicated security analysts.",
      features: ["Real-time monitoring", "Threat detection", "Incident response", "Security analytics"]
    },
    {
      icon: Globe,
      title: "Global Security Management",
      description: "Unified security management across multiple locations and cloud environments worldwide.",
      features: ["Centralized management", "Policy enforcement", "Compliance monitoring", "Risk assessment"]
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-slate-100' 
          : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3184338/pexels-photo-3184338.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Enterprise Security"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-blue-950/90' 
              : 'bg-gradient-to-r from-blue-50/90 to-purple-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <Building className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-blue-400' : 'text-blue-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Enterprise <span className={isDarkMode ? "text-blue-400" : "text-blue-600"}>Security</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Comprehensive security solutions designed for large enterprises with complex 
              infrastructure and stringent security requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-blue-500/25" 
                    : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-blue-500/25"
                }`}
              >
                <Shield className="mr-2 h-5 w-5" />
                Enterprise Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-slate-900" 
                    : "border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                }`}
              >
                View Architecture
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Solutions Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Enterprise-Grade Security Solutions
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Scalable security solutions designed for enterprise complexity and requirements
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {enterpriseSolutions.map((solution, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <solution.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{solution.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {solution.description}
                </p>
                <ul className="space-y-2">
                  {solution.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default EnterpriseSecurityPage;